'use client'

import { Target, TrendingUp, Wallet } from 'lucide-react'
import type { CaseRecord, Trade } from '@/features/cases'
import { formatCurrency, formatHeadlineCurrency } from '@/lib/format/currency'
import { MetricGrid } from '@/features/investor/components/home/metric-grid'

/** trades → win rate + capital deployed, the two numbers a trader actually watches.
 * Ported from `DashboardHome.tsx`'s `TradesHero`. Real, pre-fetched data — this component's sole
 * caller (`investor-dashboard-view.tsx`) always supplies `injectedTrades`. */
export function TradesHero({
  injectedTrades,
}: {
  /** Unused here — kept so this component's prop shape mirrors `AuditHero`'s. */
  caseRecord?: CaseRecord
  injectedTrades?: Trade[]
}) {
  const trades = injectedTrades ?? []
  const net = trades.reduce((s, t) => s + t.pnl, 0)
  const wins = trades.filter((t) => t.pnl > 0)
  const winRate = trades.length ? (wins.length / trades.length) * 100 : 0
  const capitalDeployed = trades.reduce((s, t) => s + t.qty * t.price, 0)

  return (
    <MetricGrid
      metrics={[
        {
          icon: <Wallet />,
          tint: 'var(--chart-2)',
          label: 'Capital Deployed',
          value: formatHeadlineCurrency(capitalDeployed),
          title: formatCurrency(capitalDeployed),
          footnote: 'Total traded value',
        },
        {
          icon: <Target />,
          tint: 'var(--chart-1)',
          label: 'Win Rate',
          value: `${winRate.toFixed(0)}%`,
          trend: winRate >= 50 ? 'On track' : 'Below target',
          trendUp: winRate >= 50,
          footnote: `${wins.length} of ${trades.length} trades profitable`,
        },
        {
          icon: <TrendingUp />,
          tint: 'var(--chart-positive)',
          label: 'Net P&L',
          value: `${net >= 0 ? '+' : '−'}${formatHeadlineCurrency(Math.abs(net))}`,
          title: `${net >= 0 ? '+' : '−'}${formatCurrency(Math.abs(net))}`,
          trendUp: net > 0,
          footnote: 'Across all recorded trades',
        },
      ]}
    />
  )
}
